import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Truck, Package, Boxes, ShoppingCart, ShoppingBag,
  Factory, Send, Wallet, FileBarChart, LogOut, Menu, X, Mountain, FlaskConical, Settings,
  Shield, ScrollText, KeyRound, Receipt, ClipboardList, ShieldCheck, Bell, SlidersHorizontal,
  PanelLeftClose, PanelLeftOpen, MoreHorizontal, Coins, PackagePlus,
  UserCog, CalendarCheck, CalendarDays, CalendarRange, TrendingUp, Wrench, Banknote, HardHat, Landmark, BookOpen, UserCircle, FileText, FolderOpen,
  Ship, Anchor, Newspaper, Navigation, Globe2, LineChart, Bookmark, BellRing, Plug, Gauge,
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { canAccess, ROLE_LABELS } from "../lib/permissions";
import NotificationBell from "./NotificationBell";

const NAV_GROUPS = [
  { group: "Overview", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, mobile: true },
  ] },
  { group: "Sales", items: [
    { to: "/customers", label: "Customers", icon: Users, mobile: true },
    { to: "/sales", label: "Sales", icon: ShoppingCart, mobile: true },
    { to: "/dispatch", label: "Dispatch", icon: Send },
    { to: "/samples", label: "Sample Dispatch", icon: FlaskConical },
  ] },
  { group: "Procurement", items: [
    { to: "/suppliers", label: "Suppliers", icon: Truck },
    { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingBag },
    { to: "/purchase", label: "Purchase Bills", icon: Receipt },
  ] },
  { group: "Operations", items: [
    { to: "/products", label: "Products", icon: Package },
    { to: "/inventory", label: "Inventory", icon: Boxes, mobile: true },
    { to: "/opening-stock", label: "Opening Stock", icon: PackagePlus },
    { to: "/production", label: "Production", icon: Factory },
    { to: "/job-work", label: "Job Work", icon: Wrench },
    { to: "/job-work-vendors", label: "Job Work Vendors", icon: Truck },
    { to: "/requests", label: "Requests", icon: ClipboardList, mobile: true },
    { to: "/notifications", label: "Notifications", icon: Bell },
  ] },
  { group: "Quality", items: [
    { to: "/qc-tests", label: "Material QC", icon: FlaskConical, mobile: true },
    { to: "/qc-parameters", label: "QC Parameters", icon: SlidersHorizontal },
  ] },
  { group: "Finance", items: [
    { to: "/bank", label: "Company Bank Balance", icon: Landmark },
    { to: "/payments", label: "Payments", icon: Wallet },
    { to: "/dues", label: "Dues & Payments", icon: Receipt },
    { to: "/ledger", label: "Party Ledger", icon: BookOpen },
    { to: "/expenses", label: "Expenses", icon: Coins },
    { to: "/assets", label: "Assets", icon: Wrench },
    { to: "/profit-loss", label: "Profit & Loss", icon: TrendingUp },
    { to: "/gst", label: "GST / Tax", icon: Receipt },
    { to: "/documents", label: "Documents", icon: FolderOpen },
    { to: "/reports", label: "Reports", icon: FileBarChart },
  ] },
  { group: "Human Resources", items: [
    { to: "/hr", label: "HR Dashboard", icon: LayoutDashboard },
    { to: "/employees", label: "Employees", icon: UserCog },
    { to: "/attendance/daily", label: "Daily Attendance", icon: CalendarCheck },
    { to: "/attendance/register", label: "Attendance Register", icon: CalendarRange },
    { to: "/leaves", label: "Leave Requests", icon: CalendarDays },
    { to: "/payroll-run", label: "Payroll Run", icon: Banknote },
    { to: "/payroll", label: "Salary Slips", icon: FileText },
    { to: "/my-hr", label: "My HR", icon: UserCircle },
  ] },
  { group: "Daily Workers", items: [
    { to: "/daily-workers", label: "Workers", icon: HardHat },
    { to: "/daily-attendance", label: "Worker Attendance", icon: CalendarCheck },
    { to: "/worker-attendance/register", label: "Worker Att. Register", icon: CalendarRange },
    { to: "/daily-wages", label: "Wage Statements", icon: Coins },
  ] },
  { group: "Sea Freight Intelligence", items: [
    { to: "/sea-freight", label: "Global Dashboard", icon: Ship },
    { to: "/sea-freight/rates", label: "Freight Rate Search", icon: Gauge },
    { to: "/sea-freight/news", label: "Global Shipping News", icon: Newspaper },
    { to: "/sea-freight/ports", label: "Port Intelligence", icon: Anchor },
    { to: "/sea-freight/vessels", label: "Vessel Tracking", icon: Navigation },
    { to: "/sea-freight/routes", label: "Global Route Explorer", icon: Globe2 },
    { to: "/sea-freight/analytics", label: "Rate History & Analytics", icon: LineChart },
    { to: "/sea-freight/saved-routes", label: "Saved Routes", icon: Bookmark },
    { to: "/sea-freight/quotations", label: "Freight Quotations", icon: FileText },
    { to: "/sea-freight/alerts", label: "Smart Alerts", icon: BellRing },
    { to: "/sea-freight/data-sources", label: "Data Sources & APIs", icon: Plug },
    { to: "/sea-freight/settings", label: "Freight Settings", icon: Settings },
  ] },
  { group: "Administration", items: [
    { to: "/users", label: "Users", icon: Shield },
    { to: "/request-permissions", label: "Request Access", icon: ShieldCheck },
    { to: "/audit", label: "Audit Log", icon: ScrollText },
    { to: "/company", label: "Company Settings", icon: Settings },
  ] },
];

const roleLabels = ROLE_LABELS;

const SidebarInner = ({ isCollapsed = false, isMobile = false, user, logout, navigate, setMobileOpen, setCollapsed, setLogoutOpen }) => (
  <>
    <div className={`flex items-center gap-2.5 h-16 border-b border-white/10 ${isCollapsed ? "px-0 justify-center" : "px-5"}`}>
      <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center shrink-0"><Mountain className="w-5 h-5 text-white" /></div>
      {!isCollapsed && (
        <>
          <div className="leading-tight">
            <div className="text-white font-bold text-sm font-head tracking-tight">AEROBENT</div>
            <div className="text-slate-400 text-[10px] uppercase tracking-widest">Minechem Pvt Ltd</div>
          </div>
          <div className="ml-auto"><NotificationBell /></div>
        </>
      )}
    </div>

    <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
      {NAV_GROUPS.map((grp) => {
        const items = grp.items.filter((n) => canAccess(user, n.to));
        if (!items.length) return null;
        return (
          <div key={grp.group} className="mb-1">
            {!isCollapsed && (
              <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{grp.group}</div>
            )}
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                title={isCollapsed ? n.label : undefined}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `group flex items-center gap-3 rounded-md text-sm transition-colors duration-200 ${isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} ${isActive ? "bg-white text-[#0B132B] font-semibold shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
                <n.icon className="w-[18px] h-[18px] shrink-0" /> {!isCollapsed && <span className="truncate">{n.label}</span>}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>

    <div className="px-3 py-3 border-t border-white/10">
      {!isMobile && (
        <button data-testid="sidebar-collapse-btn" onClick={() => setCollapsed((c) => !c)}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`w-full flex items-center gap-3 rounded-md text-sm text-slate-400 hover:bg-white/10 hover:text-white transition-colors mb-1 ${isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"}`}>
          {isCollapsed ? <PanelLeftOpen className="w-[18px] h-[18px]" /> : <><PanelLeftClose className="w-[18px] h-[18px]" /> <span>Collapse</span></>}
        </button>
      )}
      {!isCollapsed && (
        <div className="px-3 py-2 mb-1">
          <div className="text-white text-sm font-medium truncate">{user?.name}</div>
          <div className="text-slate-400 text-xs">{roleLabels[user?.role] || user?.role}</div>
        </div>
      )}
      <button data-testid="change-password-btn" onClick={() => { setMobileOpen(false); navigate("/change-password"); }}
        title={isCollapsed ? "Change Password" : undefined}
        className={`w-full flex items-center gap-3 rounded-md text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors ${isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"}`}>
        <KeyRound className="w-[18px] h-[18px] shrink-0" /> {!isCollapsed && "Change Password"}
      </button>
      <button data-testid="logout-btn" onClick={() => setLogoutOpen(true)}
        title={isCollapsed ? "Logout" : undefined}
        className={`w-full flex items-center gap-3 rounded-md text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-colors ${isCollapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"}`}>
        <LogOut className="w-[18px] h-[18px] shrink-0" /> {!isCollapsed && "Logout"}
      </button>
    </div>
  </>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const mobileNav = NAV_GROUPS.flatMap((g) => g.items).filter((n) => n.mobile && canAccess(user, n.to));
  const sidebarProps = { user, logout, navigate, setMobileOpen, setCollapsed, setLogoutOpen };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex fixed top-0 left-0 bottom-0 bg-[#0B132B] flex-col z-40 transition-[width] duration-200 ${collapsed ? "w-20" : "w-64"}`}>
        <SidebarInner isCollapsed={collapsed} {...sidebarProps} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-[#0B132B] flex flex-col">
            <button className="absolute top-4 right-4 text-white" onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
            <SidebarInner isMobile {...sidebarProps} />
          </aside>
        </div>
      )}

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 h-14 bg-[#0B132B] flex items-center justify-between px-4">
        <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="text-white"><Menu className="w-6 h-6" /></button>
        <span className="text-white font-bold font-head text-sm">AEROBENT MINECHEM</span>
        <NotificationBell />
      </header>

      {/* Main */}
      <main className={`pb-20 lg:pb-0 transition-[padding] duration-200 ${collapsed ? "lg:pl-20" : "lg:pl-64"}`}>
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-slate-200 flex items-center justify-around z-40">
        {mobileNav.slice(0, 4).map((n) => {
          const active = location.pathname === n.to;
          return (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottomnav-${n.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-0.5 text-[10px] font-medium ${active ? "text-[#0B132B]" : "text-slate-400"}`}>
              <n.icon className={`w-5 h-5 ${active ? "text-[#0B132B]" : "text-slate-400"}`} /> {n.label}
            </NavLink>
          );
        })}
        <button data-testid="bottomnav-more" onClick={() => setMobileOpen(true)}
          className="flex flex-col items-center gap-0.5 text-[10px] font-medium text-slate-400 hover:text-[#0B132B] transition-colors">
          <MoreHorizontal className="w-5 h-5" /> More
        </button>
      </nav>

      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>You will need to sign in again to continue working.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel-btn" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="logout-confirm-btn" onClick={() => logout()} className="bg-[#0B132B] hover:bg-[#1C2541] rounded-sm">Logout</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
